import { useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Box,
  Button,
  Link as MuiLink,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Check, Mail, Phone, Tag, X } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';

import { gql } from '@graphql/__generated__';
import type { BuyerLeadStatusCard_BuyerLeadFragment } from '@graphql/__generated__/graphql';
import { getBuyerLeadOutcomeContext } from '@modules/_shared/components/buyer-lead/buyerLeadOutcomeContext';
import { GET_BUYER_LEAD_STAGES } from '@modules/_shared/components/buyer-lead/buyerLeadQueries';
import { getBuyerLeadOutcome } from '@modules/_shared/components/buyer-lead/buyerLeadStatus';
import { CopyableText } from '@modules/_shared/components/CopyableText';
import { useBuyerLeadLostWorkflow } from '@modules/_shared/components/feed/hooks/useBuyerLeadLostWorkflow';
import { SelectField } from '@modules/_shared/components/form/RaSelect';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { TimeAgo } from '@modules/_shared/components/TimeAgo';
import { getUserDisplayName } from '@modules/_shared/components/UserCard';
import { emitFeedRefresh } from '@modules/_shared/utils/feed-refresh';
import { formatPhone } from '@modules/_shared/utils/formatting';
import {
  createLeadStageRenderOption,
  createLeadStageRenderValue,
  getStageBackgroundColor,
  selectInputStyles,
} from '@modules/_shared/utils/leadStageSelectHelpers';
import { useLocale } from '@shared/hooks/locale';
import { toGlobalId } from '@shared/utils/global-id';

export const BUYER_LEAD_STATUS_CARD_FRAGMENT = gql(/* GraphQL */ `
  fragment BuyerLeadStatusCard_buyerLead on buyer_leads {
    id
    created_at
    source
    stage_id
    lead_stage {
      id
      name
      label
      status
    }
    user {
      id
      first_name
      last_name
      full_name
      phone_numbers(order_by: [{ primary: desc }, { created_at: asc }]) {
        id
        number
        primary
      }
      emails(
        order_by: [{ primary: desc }, { verified: desc }, { created_at: desc }]
      ) {
        id
        email
        primary
      }
    }
  }
`);

const UPDATE_BUYER_LEAD_STAGE_FROM_STATUS_CARD = gql(/* GraphQL */ `
  mutation UpdateBuyerLeadStageFromStatusCard($id: uuid!, $stageId: uuid!) {
    update_buyer_leads_by_pk(
      pk_columns: { id: $id }
      _set: { stage_id: $stageId }
    ) {
      id
      stage_id
      lead_stage {
        id
        name
        label
        status
      }
    }
  }
`);

const InfoRow: React.FC<{
  icon: React.ReactNode;
  text: string;
  copyable?: boolean;
}> = ({ icon, text, copyable = false }) => {
  const content = (
    <Typography variant="body2" noWrap sx={{ color: 'text.secondary' }}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      <Box sx={{ color: 'text.secondary', flexShrink: 0, display: 'flex' }}>
        {icon}
      </Box>
      {copyable ? <CopyableText text={text}>{content}</CopyableText> : content}
    </Box>
  );
};

type Props = {
  buyerLead: BuyerLeadStatusCard_BuyerLeadFragment;
  canEdit: boolean;
};

export const BuyerLeadStatusCard = ({ buyerLead, canEdit }: Props) => {
  const { t } = useLocale();
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [pendingOutcome, setPendingOutcome] = useState<'won' | 'lost' | null>(
    null,
  );

  const { data: stagesData } = useQuery(GET_BUYER_LEAD_STAGES);
  const stages = useMemo(() => stagesData?.lead_stages ?? [], [stagesData]);

  const stageOptions = useMemo(
    () => stages.map(stage => ({ value: stage.id, label: stage.label ?? '' })),
    [stages],
  );

  const currentStageStatus = useMemo(
    () => stages.find(stage => stage.id === buyerLead.stage_id)?.status,
    [stages, buyerLead.stage_id],
  );

  const stageBackgroundColor = useMemo(
    () => getStageBackgroundColor(currentStageStatus, palette),
    [currentStageStatus, palette],
  );

  const [updateStage, { loading: updatingStage }] = useMutation(
    UPDATE_BUYER_LEAD_STAGE_FROM_STATUS_CARD,
    { onError: setErrorLike, onCompleted: () => emitFeedRefresh() },
  );

  const { lostReasonDialog, triggerWon, triggerLost } =
    useBuyerLeadLostWorkflow({ setErrorLike });

  const user = buyerLead.user;
  const displayName = getUserDisplayName(user);
  const profileUrl = `/v1/users/${toGlobalId('User', user.id)}/overview`;
  const phone = user.phone_numbers[0]?.number ?? null;
  const email = user.emails[0]?.email ?? null;
  const { isWon, isLost } = getBuyerLeadOutcome(currentStageStatus);

  const workflowContext = getBuyerLeadOutcomeContext(buyerLead.id);

  const handleOutcome = async (action: 'won' | 'lost') => {
    setPendingOutcome(action);
    try {
      await (action === 'won' ? triggerWon : triggerLost)(workflowContext);
    } finally {
      setPendingOutcome(null);
    }
  };

  return (
    <>
      <ListingAccordion defaultExpanded disableCollapse>
        <Stack sx={{ gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <MuiLink
                component={RouterLink}
                to={profileUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {displayName}
              </MuiLink>
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                pt: 0.5,
              }}
            >
              <TimeAgo dateString={buyerLead.created_at} addSuffix />
            </Typography>
          </Box>

          {phone != null && (
            <InfoRow
              icon={<Phone size={16} />}
              text={formatPhone(phone) ?? phone}
              copyable
            />
          )}
          {email != null && (
            <InfoRow icon={<Mail size={16} />} text={email} copyable />
          )}
          {buyerLead.source != null && (
            <InfoRow icon={<Tag size={16} />} text={buyerLead.source} />
          )}

          <Box
            sx={{
              display: 'flex',
              flexDirection: {
                xs: 'column',
                sm: 'row',
                md: 'column',
                lg: 'row',
              },
              gap: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
                '& .MuiOutlinedInput-root': {
                  ...selectInputStyles,
                  background: stageBackgroundColor,
                },
              }}
            >
              <SelectField
                field={{
                  value: buyerLead.stage_id ?? '',
                  onChange: e =>
                    updateStage({
                      variables: {
                        id: buyerLead.id,
                        stageId: e.target.value as string,
                      },
                    }),
                  onBlur: () => {},
                  name: 'stage_id',
                  ref: () => {},
                }}
                options={stageOptions}
                disabled={!canEdit}
                loading={updatingStage}
                renderValue={createLeadStageRenderValue(stages)}
                renderOption={createLeadStageRenderOption(stages)}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }}>
              <Button
                variant="soft"
                color="error"
                startIcon={<X />}
                loading={pendingOutcome === 'lost'}
                loadingPosition="start"
                disabled={!canEdit || isWon || isLost || pendingOutcome != null}
                onClick={() => handleOutcome('lost')}
              >
                {t('Lost')}
              </Button>
              <Button
                variant="soft"
                color="success"
                startIcon={<Check />}
                loading={pendingOutcome === 'won'}
                loadingPosition="start"
                disabled={!canEdit || isWon || pendingOutcome != null}
                onClick={() => handleOutcome('won')}
              >
                {t('Won')}
              </Button>
            </Box>
          </Box>
        </Stack>
      </ListingAccordion>
      {lostReasonDialog}
      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </>
  );
};
